import { useMemo } from 'react';
import { useMetros } from '@/hooks/useReferenceData';
import { useTenant } from '@/hooks/useTenant';
import { countryCentre } from '@/components/map/googleMaps';
import type { MapArea, MapView } from '@/components/map/FleetMap';

/**
 * The company's service areas as map circles, and where an empty map should look: the first
 * area with a centre, else the company's country. Never a hard-coded city (Longhorn is in the US).
 */
export function useMapAreas(): { areas: MapArea[]; fallback: MapView } {
  const { data: metros = [] } = useMetros(true);
  const { company } = useTenant();
  const country = company?.country ?? null;

  return useMemo(() => {
    const areas: MapArea[] = metros
      .filter((m) => m.center_lat != null && m.center_lng != null)
      .map((m) => ({
        id: m.id,
        name: m.name,
        lat: Number(m.center_lat),
        lng: Number(m.center_lng),
        radiusKm: m.radius_km != null ? Number(m.radius_km) : null,
      }));
    const fallback: MapView = areas.length > 0
      ? { center: { lat: areas[0].lat, lng: areas[0].lng }, zoom: 10 }
      : countryCentre(country);
    return { areas, fallback };
  }, [metros, country]);
}
